import React, { useState } from "react";
import {
    useCreateAutomation,
    useDeleteAutomation,
    useGetAutomation,
    useGetAutomationCategory,
    useUpdateAutomation,
} from "../../hooks/data/data_automation";
import { CustomCircularProgress } from "../../components/core/CustomCircularProgress";
import { ErrorTryRefetch } from "../../components/core/ErrorTryRefetch";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Typography,
} from "@mui/material";
import { CustomSnackbar } from "../../components/core/Snackbar";
import { IFeedback } from "../../interfaces/IFeedback";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useGetOfferState } from "../../hooks/data/data_offer";
import { useGetProjectState } from "../../hooks/data/data_project";
import { useGetTaskPriority, useGetTaskState, useGetTaskType } from "../../hooks/data/data_task";
import { useGetBillState } from "../../hooks/data/data_bill";
import { IAutomation, IAutomationChangeState } from "../../interfaces/IAutomation";
import { AutomationEdit, getEmptyAutomation } from "../../components/automation/AutomationEdit";
import { useGetUser } from "../../hooks/data/data_profile";
import { IOfferState } from "../../interfaces/IOfferState";
import { IProjectState } from "../../interfaces/IProjectState";
import { IBillState } from "../../interfaces/IBillState";
import { ITaskState } from "../../interfaces/ITaskState";
import { Check, Close, Delete } from "@mui/icons-material";

export const getAutomationGetStateValue = (
    automation: IAutomation | IAutomationChangeState,
    offerStateArray: IOfferState[] | undefined,
    projectStateArray: IProjectState[] | undefined,
    taskStateArray: ITaskState[] | undefined,
    billStateArray: IBillState[] | undefined,
) => {
    if (automation.AutomationCategory === 1) {
        return projectStateArray?.find((cur) => cur.id === automation.ProjectState)?.projectState;
    } else if (automation.AutomationCategory === 2) {
        return offerStateArray?.find((cur) => cur.id === automation.OfferState)?.offerState;
    } else if (automation.AutomationCategory === 3) {
        return taskStateArray?.find((cur) => cur.id === automation.TaskState)?.taskState;
    } else if (automation.AutomationCategory === 4) {
        return billStateArray?.find((cur) => cur.id === automation.BillState)?.billState;
    } else {
        return <>Fehler!</>;
    }
};

export const AutomationOverview: React.FC = () => {
    const { isPendingUpdateAutomation, updateAutomation } = useUpdateAutomation();
    const { createAutomation, isPendingCreateAutomation } = useCreateAutomation();
    const { deleteAutomation, isPendingDeleteAutomation } = useDeleteAutomation();

    const isPending = isPendingCreateAutomation || isPendingUpdateAutomation || isPendingDeleteAutomation;

    const { automationArary, isLoadingAutomationArray, refetchAutomationArray } = useGetAutomation();
    const { offerStateArray, isLoadingOfferState, refetchOfferState } = useGetOfferState();
    const { projectStateArray, isLoadingProjectStateArray, refetchProjectState } = useGetProjectState();
    const { taskStateArray, isLoadingTaskState, refetchTaskStateTask } = useGetTaskState();
    const { billStateArray, isLoadingBillState, refetchBillStateTask } = useGetBillState();
    const { automationCategoryArary, isLoadingAutomationCategoryArray, refetchAutomationCategoryArray } =
        useGetAutomationCategory();
    const { roleArray, isLoadingRoleArray, refetchRoleArray } = useGetUser();
    //
    const { isLoadingTaskPriority, refetchTaskPriorityTask, taskPriorityArray } = useGetTaskPriority();
    const { isLoadingTaskType, refetchTaskTypeTask, taskTypeArray } = useGetTaskType();
    //
    const [feedback, setFeedback] = useState<IFeedback>({
        open: false,
        message: "Gespeichert!",
        severity: "success",
    } as IFeedback);
    //
    const [editAutomation, setEditAutomation] = useState<IAutomation | null>(null);

    const refetchAll = () => {
        refetchAutomationArray();
        refetchAutomationCategoryArray();
        refetchOfferState();
        refetchProjectState();
        refetchTaskStateTask();
        refetchBillStateTask();
        refetchRoleArray();
        //
        refetchTaskPriorityTask();
        refetchTaskTypeTask();
    };

    const handleUpload = async () => {
        if (editAutomation !== null) {
            try {
                if (editAutomation.id > -1) {
                    await updateAutomation(editAutomation);
                    setEditAutomation(null);
                } else {
                    const createdAutomation = await createAutomation(editAutomation);
                    setEditAutomation(createdAutomation);
                }
                setFeedback({ open: true, message: "Erfolgreich gespeichert!", severity: "success" });

                refetchAutomationArray();
            } catch (error) {
                console.error(error);
                setFeedback({ open: true, message: "Es ist ein Fehler aufgeretten!", severity: "error" });
            }
        }
    };
    const handleDelete = async () => {
        if (editAutomation !== null) {
            try {
                await deleteAutomation(editAutomation.id);
                setEditAutomation(null);
                setFeedback({ open: true, message: "Erfolgreich gelöscht!", severity: "success" });
                refetchAutomationArray();
            } catch (error) {
                console.error(error);
                setFeedback({ open: true, message: "Es ist ein Fehler aufgeretten!", severity: "error" });
            }
        }
    };

    const handleNew = () => {
        setEditAutomation(getEmptyAutomation());
    };

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "Nr.",
            width: 70,
        },

        {
            field: "AutomationMailArray",
            headerName: "E-Mails",
            valueGetter: (params) => params.value.length,
            width: 100,
        },

        {
            field: "AutomationTaskArray",
            headerName: "Aufgaben",
            valueGetter: (params) => params.value.length,
            width: 100,
        },

        {
            field: "AutomationChangeStateArray",
            headerName: "Statusänderungen",
            valueGetter: (params) => params.value.length,
            width: 100,
        },

        {
            field: "is_activ",
            headerName: "Aktiv",
            renderCell: (params) => params.value === true ? <Check/> : <Close/>,
            width: 100,
        },


        {
            field: "AutomationCategory",
            headerName: "Kategorie",
            valueGetter: (params) =>
                automationCategoryArary?.find((category) => category.id === Number(params.value))?.automationCategory,
            width: 250,
        },

        {
            field: "State",
            headerName: "Ausgeführt beim Status",
            valueGetter: (params) =>
                getAutomationGetStateValue(
                    params.row,
                    offerStateArray,
                    projectStateArray,
                    taskStateArray,
                    billStateArray,
                ),
            width: 250,
        },
        {
            field: "title",
            headerName: "Titel",
            flex: 1,
        },
    ];

    if (
        isLoadingAutomationArray ||
        isLoadingAutomationCategoryArray ||
        isLoadingOfferState ||
        isLoadingProjectStateArray ||
        isLoadingTaskState ||
        isLoadingBillState ||
        isLoadingRoleArray ||
        //
        isLoadingTaskPriority ||
        isLoadingTaskType
    ) {
        return <CustomCircularProgress />;
    }
    if (
        !automationArary ||
        !automationCategoryArary ||
        !offerStateArray ||
        !projectStateArray ||
        !taskStateArray ||
        !billStateArray ||
        !roleArray ||
        //
        !taskPriorityArray ||
        !taskTypeArray
    ) {
        return <ErrorTryRefetch refetchFunction={refetchAll} />;
    }

    return (
        <>
            {editAutomation !== null && (
                <Dialog open onClose={() => setEditAutomation(null)} maxWidth="md" fullWidth>
                    <DialogContent>
                        <>
                            {editAutomation.id > 0 && (
                                <IconButton sx={{ float: "right" }} onClick={handleDelete}>
                                    <Delete />
                                </IconButton>
                            )}
                            <AutomationEdit
                                key={`edit-automation-${editAutomation.id}`}
                                automation={editAutomation}
                                setAutomation={setEditAutomation}
                                automationCategoryArray={automationCategoryArary}
                                billStateArray={billStateArray}
                                offerStateArray={offerStateArray}
                                projectStateArray={projectStateArray}
                                taskStateArray={taskStateArray}
                                roleArray={roleArray}
                                //
                                taskPriorityArray={taskPriorityArray}
                                taskTypeArray={taskTypeArray}
                            />
                        </>
                    </DialogContent>
                    <DialogActions>
                        <Button disabled={isPending} variant="outlined" onClick={() => setEditAutomation(null)}>
                            Abbruch
                        </Button>
                        <Button variant="contained" disabled={isPending} onClick={handleUpload}>
                            {isPending ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <span>Speichern</span>
                                    <CircularProgress size={20} />
                                </Box>
                            ) : (
                                "Speichern"
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <Box>
                <Typography variant="h5">
                    Automatisierungen
                    <Button sx={{ float: "right" }} variant="outlined" onClick={handleNew}>
                        Automatisierung hinzufügen
                    </Button>
                </Typography>
            </Box>

            <CustomSnackbar feedback={feedback} setFeedback={setFeedback} />
            <Box sx={{ mt: 2 }} />
            <DataGrid
                rows={automationArary}
                columns={columns}
                //getRowId={(row) => row.id}
                onRowClick={(params, event, details) => {
                    let testObject = automationArary.find((x) => x.id === params.id);
                    if (testObject !== undefined) {
                        setEditAutomation(testObject);
                    }
                }}
                autoHeight
            />
        </>
    );
};
